import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';

const PublicWebSocketContext = createContext(null);

export const PublicWebSocketProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [mounted, setMounted] = useState(false);
  const socketRef = useRef(null);

  // Set mounted state
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    // Small delay to ensure DOM is ready
    const initTimer = setTimeout(() => {
      console.log('[Public WS] Initializing connection...');
      
      // Connect without authentication (anonymous/read-only)
      socketRef.current = io(process.env.REACT_APP_WS_URL || `http://${window.location.hostname}:5000`, {
        auth: {},
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
        forceNew: false, // Reuse existing connection
        transports: ['websocket', 'polling'] // Prefer websocket
      });

      // Connection events
      socketRef.current.on('connect', () => {
        console.log('[Public WS] Connected:', socketRef.current.id);
        setConnected(true);
        setError(null);
      });

      socketRef.current.on('connection:success', (data) => {
        console.log('[Public WS] Connection success:', data);
      });

      socketRef.current.on('disconnect', (reason) => {
        console.log('[Public WS] Disconnected:', reason);
        setConnected(false);
      });

      socketRef.current.on('reconnect', (attemptNumber) => {
        console.log('[Public WS] Reconnected after', attemptNumber, 'attempts');
        setConnected(true);
      });

      socketRef.current.on('connect_error', (err) => {
        console.error('[Public WS] Connection error:', err.message);
        setError(err.message);
        setConnected(false);
      });

      socketRef.current.on('error', (err) => {
        console.error('[Public WS] Error:', err);
        setError(err.message || 'WebSocket error');
      });
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(initTimer);
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [mounted]);

  const value = {
    socket: socketRef.current,
    connected,
    error,
    isPublic: true // Flag to identify this is a public connection
  };

  return (
    <PublicWebSocketContext.Provider value={value}>
      {children}
    </PublicWebSocketContext.Provider>
  );
};

export const usePublicWebSocketContext = () => {
  const context = useContext(PublicWebSocketContext);
  if (!context) {
    throw new Error('usePublicWebSocketContext must be used within PublicWebSocketProvider');
  }
  return context;
};
